import { forwardRef, useEffect, useRef } from "react";

import { useDispatch } from "react-redux";
import classNames from "classnames";
import videojs from "video.js";

import { registerPlayerComponents } from "./PlayerComponents";

import usePause from "../../hooks/usePause";

import { setPlayingPlayerRef } from "@redux/player/actions";

import { isVideoFile } from "@helpers/files";
import { controlBarHideDelay } from "@const/player";

/** Needed styles for video component
 * it also needs to create a separate styles file to restyle different 'vjs-' elements
 * */
import "video.js/dist/video-js.css";
import { ReactComponent as AudioPostPlaceholder } from "@assets/ic-audio-placeholder.svg";

const VideoJsPlayer = forwardRef(
  (
    {
      sources,
      poster,
      onReady,
      className,
      landing,
      hideFullscreen = false,
      fluid = false,
      preload = "auto",
      style,
      isAudio,
    },
    externalRef,
  ) => {
    const videoRef = useRef(null);
    const { ref, playerRef } = usePause();
    const dispatch = useDispatch();

    useEffect(() => {
      registerPlayerComponents();
    }, []);
    const playHandler = () => {
      dispatch(setPlayingPlayerRef(playerRef.current));
    };

    useEffect(() => {
      /** Check for file type (video/, audio/ etc */
      const isVideo = sources?.some(isVideoFile);

      // ! for some reason `videojs` can play `video/quicktime` (.mov files) only as `video/mp4`
      sources?.forEach((source) => {
        if (source.type === "video/quicktime") source.type = "video/mp4";
      });

      const videoJsOptions = {
        autoplay: false,
        bigPlayButton: true,
        controls: true,
        responsive: true,
        fluid,
        preload,
        inactivityTimeout: controlBarHideDelay,
        children: {
          controlBar: {
            progressControl: {
              seekBar: {
                loadProgressBar: true,
                playProgressBar: true,
                mouseTimeDisplay: false,
              },
            },
            backwardButton: !landing,
            playToggle: true,
            forwardButton: !landing,
            currentTimeDisplay: true,
            timeDivider: true,
            durationDisplay: true,
            remainingTimeDisplay: true,
            pictureInPictureToggle: false,
            volumePanel: {
              volumeLevelTooltip: true,
              muteToggle: true,
            },
            /** do not show fullscreen button if file type is audio */
            fullscreenToggle: !landing && isVideo && !hideFullscreen,
            liveDisplay: false,
            seekToLive: false,
          },
        },
      };

      if (!playerRef.current) {
        const videoElement = videoRef.current;

        if (!videoElement) {
          return;
        }

        playerRef.current = videojs(
          videoElement,
          { ...videoJsOptions, ...sources },
          () => {
            onReady?.(playerRef.current);
          },
        );
      }
      if (externalRef) {
        externalRef.current = playerRef.current;
      }

      if (!playerRef.current || typeof playerRef.current.on !== "function") {
        console.warn(
          "playerRef.current is not ready or does not support .on()",
        );
        return;
      }
      playerRef.current.on("play", playHandler);

      playerRef.current.autoplay(videoJsOptions.autoplay);
      playerRef.current.src(sources);
      /** Show needed poster if file type is audio or needed poster for video */
      playerRef.current.poster(
        isVideo ? poster : poster || AudioPostPlaceholder,
      );
    }, [onReady, poster, sources, landing, fluid, playerRef, externalRef]);

    useEffect(() => {
      return () => {
        if (playerRef?.current?.dispose) {
          // playerRef?.current?.dispose?.();
          playerRef.current = null;
          if (externalRef) {
            externalRef.current = null;
          }
        }
      };
    }, []);

    return (
      /** needed data attribute */
      <div
        data-vjs-player
        ref={ref}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        {isAudio && (
          <AudioPostPlaceholder className="honeymakers-player-audio-placeholder" />
        )}
        {/** video-js - should be. vjs-big-play-centered - not necessary could be variants */}
        <video
          ref={videoRef}
          playsInline
          className={classNames(
            "honeymakers-player video-js vjs-default-skin vjs-big-play-centered",
            landing && "honeymakers-player video-js-landing",
            className,
          )}
        />
      </div>
    );
  },
);

VideoJsPlayer.displayName = "VideoJsPlayer";

export default VideoJsPlayer;
